<template>
  <EcxToaster />
  <GlobalLoader />
  <SpreadSheetErrorModal />
  <GlobalBanner />

  <ManagerSecurityReminderModal
    :open="showSecurityModal"
    @confirm="onSecurityModalConfirm"
  />

  <div class="flex h-screen max-h-screen">
    <EcxSidebar
      v-if="route.meta.needsSidebar"
    />

    <main
      id="main-view"
      class="w-full h-full flex-1 overflow-y-scroll overflow-x-hidden"
    >
      <EcxToolbar
        v-if="route.meta.needsToolbar"
      />

      <RouterView
        :key="$route.path"
      />
    </main>
  </div>
  <!-- <EcxSmallScreenBanner  -->
    <!-- v-if="isntVirtualCardRoute" -->
    <!-- :title="t('views.small_screen_banner.title')" -->
    <!-- :message="smallScreenMessage" -->
    <!-- :exit-button-text="t('views.small_screen_banner.exit_button')" -->
    <!-- logo-src="/assets/images/ecx_logo.webp" -->
  <!-- /> -->
</template>

<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, watch } from 'vue';
  import { useUserStore } from '@/stores/user';
  import { useSessionStore } from '@/stores/session';
  import { RouterView, useRoute, useRouter } from 'vue-router';
  
  import EcxToaster from './components/EcxToaster.vue';
  import GlobalLoader from './components/GlobalLoader.vue';
  import GlobalBanner from './components/GlobalBanner.vue';
  import EcxToolbar from './components/navigation/EcxToolbar.vue';
  import EcxSidebar from './components/navigation/EcxSidebar.vue';
  import Intercom, { boot, shutdown } from '@intercom/messenger-js-sdk';
  import SpreadSheetErrorModal from '@/components/EcxSpreadSheetErrorModal.vue';
  import ManagerSecurityReminderModal from '@/components/manager/ManagerSecurityReminderModal.vue';
  
  import { EcxSmallScreenBanner } from '@ecx/ui';
  import { useI18n } from '@/composables/useI18n';

  const { t } = useI18n();

  const smallScreenMessage = computed(() => 
    `${t('views.small_screen_banner.message.we_detected_that')} ${t('views.small_screen_banner.message.your_screen_is_too_small')} ${t('views.small_screen_banner.message.is_smaller_than_recommended')}`
  );

  const sessionStore = useSessionStore();
  const { hasSession, email: sessionEmail } = storeToRefs(sessionStore);
  const userStore = useUserStore();
  const { user, profileContactIncomplete } = storeToRefs(userStore);

  const route = useRoute();
  const router = useRouter();

  const isPublicAuthPath = (path: string, name: string | symbol | null | undefined): boolean => {
    const publicPaths = ['/login', '/auth-callback', '/redefinir-senha', '/esqueci-minha-senha', '/validar-token'];
    if (publicPaths.some((publicPath) => path === publicPath || path.startsWith(`${publicPath}?`) || path.startsWith(`${publicPath}/`))) {
      return true;
    }
    if (typeof name === 'string') {
      if (['auth-callback', 'redefinir-senha', 'esqueci-minha-senha'].includes(name)) {
        return true;
      }
      if (name.startsWith('autenticacao-2-fatores')) {
        return true;
      }
    }
    return false;
  };

  const showSecurityModal = computed(() => {
    if (!hasSession.value) {
      return false;
    }
    if (!profileContactIncomplete.value) {
      return false;
    }
    if (isPublicAuthPath(route.path, route.name)) {
      return false;
    }
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    if (params.get('auth_from') === 'ecx-auth') {
      return false;
    }
    if (route.name === 'settings-edit-account') {
      return false;
    }
    return true;
  });

  const onSecurityModalConfirm = () => {
    router.push({ name: 'settings-edit-account' });
  };

  const isntVirtualCardRoute = computed(() => !router.currentRoute.value.path.includes('/swap-virtual-card'));

  const INTERCOM_APP_ID = 'eq4smcoy';
  const INTERCOM_SCRIPT_ID = '_intercom_npm_loader';

  const intercomWidgetLoaderExists = () =>
    typeof document !== 'undefined' && Boolean(document.getElementById(INTERCOM_SCRIPT_ID));

  watch(
    () =>
      [
        hasSession.value,
        user?.value?.id,
        user?.value?.email,
        user?.value?.name,
        sessionEmail.value,
      ] as const,
    ([hasSessionValue, userId, userEmail, userName, emailFromSession], previous) => {
      const previousHadSession = previous?.[0] ?? false;

      if (hasSessionValue) {
        const emailForIntercom = (userEmail || emailFromSession || '').trim();
        if (!emailForIntercom) {
          return;
        }

        const rawUserId = userId;
        const userIdForIntercom =
          rawUserId != null && rawUserId !== 0 ? String(rawUserId) : undefined;

        const nameForIntercom = (userName || '').trim() || undefined;

        const payload = {
          app_id: INTERCOM_APP_ID,
          email: emailForIntercom,
          name: nameForIntercom,
          user_id: userIdForIntercom,
          hide_default_launcher: false,
        };

        if (intercomWidgetLoaderExists()) {
          boot(payload);
        } else {
          Intercom(payload);
        }
        return;
      }

      if (previousHadSession && !hasSessionValue) {
        router.push({ path: '/login', query: { logout: 'true' } });
        shutdown();
      }
    },
    { immediate: true },
  );

</script>

<style>
@import "@/assets/base.css";
</style>
